import { SquarePlayer } from "@/components/molecules/Player/SquarePlayer/SquarePlayer";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { convertPlayerPrice } from "@/functions/convertPlayerPrice";
import { getPriceType } from "@/functions/getPriceType";
import {
  activeSquadWithCircle as activeSquadIcon,
  transferList as transferListIcon,
  untradeableOnly as untradeablesIcon,
} from "@/icons";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  ManuallyAddPlayerProperties,
  addPlayerToClub,
  myPlayersSelector,
  updateManuallyAddPlayerProperties,
} from "@/redux/myClub/myPlayersSlice";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { AppDispatch } from "@/redux/store";
import { useDispatch, useSelector } from "react-redux";

interface AddPlayersDropdownProps {
  onClose: () => void;
  result: SearchedPlayer[];
}
export const AddPlayersDropdown = ({
  onClose,
  result,
}: AddPlayersDropdownProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { manuallyAddPlayerProperties } = useSelector(myPlayersSelector);

  const handleAddPlayer = (player: SearchedPlayer) => {
    dispatch(addPlayerToClub(player));
    onClose();
  };

  return (
    <div className="absolute bottom-0 mobile:top-full top-72 mobile:mt-2 w-full mobile:w-96 left-0 mobile:left-auto mobile:right-0 bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-lg py-2 z-20">
      <div className="flex flex-row justify-between w-full -mt-2 border-t border-l border-r border-white/20 rounded-t-lg border-b p-1 bg-primary-dark items-center ">
        <div className="text-white/80 text-sm">Mark player as: </div>
        <div className="flex flex-row gap-x-2">
          <button
            className={`w-6 h-6 my-auto ${
              !manuallyAddPlayerProperties.untradeable && " opacity-30"
            }`}
            onClick={() =>
              dispatch(
                updateManuallyAddPlayerProperties({
                  untradeable: !manuallyAddPlayerProperties.untradeable,
                  transferList: manuallyAddPlayerProperties.transferList,
                  activeSquad: manuallyAddPlayerProperties.activeSquad,
                  sbcStorage: manuallyAddPlayerProperties.sbcStorage,
                }),
              )
            }
          >
            {untradeablesIcon}
          </button>
          <button
            className={`w-6 h-6 my-auto ${
              !manuallyAddPlayerProperties.activeSquad && " opacity-30"
            }`}
            onClick={() =>
              dispatch(
                updateManuallyAddPlayerProperties({
                  untradeable: manuallyAddPlayerProperties.untradeable,
                  transferList: manuallyAddPlayerProperties.transferList,
                  activeSquad: !manuallyAddPlayerProperties.activeSquad,
                  sbcStorage: manuallyAddPlayerProperties.sbcStorage,
                }),
              )
            }
          >
            {activeSquadIcon}
          </button>
          <button
            className={`w-6 h-6 my-auto ${
              !manuallyAddPlayerProperties.transferList && " opacity-30"
            }`}
            onClick={() =>
              dispatch(
                updateManuallyAddPlayerProperties({
                  untradeable: manuallyAddPlayerProperties.untradeable,
                  transferList: !manuallyAddPlayerProperties.transferList,
                  activeSquad: manuallyAddPlayerProperties.activeSquad,
                  sbcStorage: manuallyAddPlayerProperties.sbcStorage,
                }),
              )
            }
          >
            {transferListIcon}
          </button>
        </div>
      </div>
      <div className="relative">
        <ScrollArea className="max-h-128 h-128">
          {(result || []).map((player) => (
            <AddPlayerToImportPlayerEntry
              key={player.resourceId}
              player={player}
              playerProperties={manuallyAddPlayerProperties}
              onClick={handleAddPlayer}
            />
          ))}
          <ScrollBar
            orientation="vertical"
            className="cursor-grab"
            forceMount={true}
          />
        </ScrollArea>
      </div>
    </div>
  );
};

const AddPlayerToImportPlayerEntry = ({
  player,
  playerProperties,
  onClick,
}: {
  player: SearchedPlayer;
  playerProperties: ManuallyAddPlayerProperties;
  onClick: (player: SearchedPlayer) => void;
}) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const playerPriceFrom = getPriceType(
    player.price,
    player.sbcPrice,
    player.isSbcPlayer,
    player.isObjectivePlayer,
  );

  return (
    <button
      className="h-16 flex flex-row items-center gap-3 bg-primary-medium-dark border-b border-white/20 w-full"
      onClick={() => onClick(player)}
    >
      <div className="relative max-mobile:scale-[125%] ml-2 mobile:ml-0 w-10 h-10 mobile:w-15 mobile:h-15 flex items-center justify-center">
        <SquarePlayer
          metalId={player.metalId}
          versionId={player.versionId}
          playerName={player.cardName}
          playerUrl={player.playerUrl}
        />
      </div>
      <div className="flex flex-col items-center">
        <div className="text-white font-semibold min-w-8">{player.rating}</div>
        <div className="text-white/80 text-sm">{player.preferredPosition}</div>
      </div>
      <div className="flex flex-col items-start justify-start grow">
        <div className="flex flex-row items-center justify-between w-full">
          <div className="text-white text-sm max-w-32 truncate">
            {player.cardName}
          </div>
          <div className="flex flex-row items-center gap-x-1 mr-0">
            {playerProperties.untradeable && (
              <div className="w-4 h-4">{untradeablesIcon}</div>
            )}
            {playerProperties.activeSquad && (
              <div className="w-4 h-4">{activeSquadIcon}</div>
            )}
            {playerProperties.transferList && (
              <div className="w-4 h-4">{transferListIcon}</div>
            )}
          </div>
        </div>
        <div className="text-xs text-primary-washed-blue">
          {versionAssets[player.versionId]?.name}
        </div>
        <div className="flex flex-row items-center gap-x-1">
          <img
            className="w-3 h-3"
            src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/misc/${
              playerPriceFrom === "sbc"
                ? "sbc.png"
                : playerPriceFrom === "xp"
                  ? "xp_not_cached.png"
                  : "coin.png"
            }`}
            alt="price"
          />
          <div className="text-xs">
            {!(playerPriceFrom === "xp") &&
              convertPlayerPrice(
                playerPriceFrom === "sbc"
                  ? player.sbcPrice || 0
                  : player.price || 0,
                true,
              )}
          </div>
        </div>
      </div>
      <div className="ml-auto flex flex-col items-center mr-4 gap-1">
        <img
          className="w-6"
          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/countries/${player.countryId}.png`}
          alt="flag"
        />
        <img
          className="h-6"
          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/clubs/dark/${player.clubId}.png`}
          alt="flag"
        />
      </div>
    </button>
  );
};
